import { Modal } from 'antd';
import ExclamationMarkCart from 'assets/Icons/ExclamationMarkCart';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import RenderIf from '@/components/Common/RenderIf';
import { montserrat } from '@/components/Layout';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';

interface Props {
  retryOrder?: () => void;
  isDigitalProduct?: boolean;
  open?: boolean;
  onCancel?: () => void;
  onCodClick?: () => void;
  onDoNotPlaceOrderClick?: () => void;
  isPreOrder?: boolean;
  convertCodLoading?: boolean;
  isIndiaShipping?: boolean;
  hasNonCodItems?: boolean;
}

const CancelPaymentModal: React.FC<Props> = ({
  retryOrder,
  isDigitalProduct,
  open,
  onCancel,
  onCodClick,
  onDoNotPlaceOrderClick,
  isPreOrder,
  convertCodLoading,
  isIndiaShipping,
  hasNonCodItems = false,
}) => {
  const router = useRouter();

  // Static flags — replace these with real logic when ready

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      maskClosable={false}
      closable={false}
      centered={true}
      footer={null}
      styles={{
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(8px)',
        },
      }}
      className='md:max-w-[505px] customModalNoPadding customMaskModal'
      keyboard={false}
      style={{
        fontFamily: montserrat.style.fontFamily,
      }}
    >
      <div className='flex flex-col gap-y-3 bg-[#000000E5] border rounded-2xl border-[#303030] px-6 pb-12'>
        <span className='text-center flex justify-center mt-16 xs:mt-20 md:mt-24'>
          <ExclamationMarkCart className='text-[#DC9646] w-16 h-16' />
        </span>

        <p className='text-[#A0A0A0] text-center font-semibold text-base mt-2'>
          Your Order Failed
        </p>

        <p className='text-lg font-semibold text-white text-center px-5 md:px-16 mt-8'>
          Don't worry, all your filled information is safe with us.
        </p>

        <p className='md:px-24 text-center mt-0.5 text-[#A7A7A7]'>
          <ShouldRender check={!isDigitalProduct && !hasNonCodItems}>
            Choose Cash on Delivery (COD) or{' '}
          </ShouldRender>
          Retry to complete your order.
        </p>

        <div className='flex flex-col-reverse md:flex-row gap-y-2 xs:gap-x-3 md:gap-y-0 md:gap-x-3.5 mt-8 xs:mt-8 md:mt-10 relative'>
          {/* Retry Order */}
          <ButtonDefault
            size={2}
            roundedSm={true}
            className={clsx('border border-[#2C2C2C] flex-1')}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              retryOrder?.();
            }}
            variant={
              isDigitalProduct ? ButtonVariants.WHITE : ButtonVariants.GRAY
            }
          >
            <span
              className={clsx(
                isDigitalProduct ? 'text-black' : 'text-white',
                'flex text-base gap-x-2 md:px-4 md:py-0.5 whitespace-nowrap',
              )}
            >
              Retry Order
            </span>
          </ButtonDefault>

          {/* COD button */}
          <RenderIf condition={!isDigitalProduct && !isPreOrder && isIndiaShipping === true && !hasNonCodItems}>
            <ButtonDefault
              loading={convertCodLoading}
              disabled={convertCodLoading}
              size={2}
              roundedSm={true}
              spinColor='black'
              enableScaling={false}
              className='flex-1'
              variant={ButtonVariants.WHITE}
              onClick={onCodClick}
            >
              Complete with COD
            </ButtonDefault>
          </RenderIf>
        </div>

        {/* "Don't place my Order" — below buttons for non-digital */}
        <p
          onClick={() => {
            onDoNotPlaceOrderClick?.()
            router.push('my-orders')
          }}
          className='text-sm md:text-base hover:text-white cursor-pointer underline font-medium text-[#737373] text-center mt-7 underline-offset-2'
        >
          Don't place my Order
        </p>
      </div>
    </Modal>
  );
};

export default CancelPaymentModal;
